import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Table, Tag, Progress, Button, message, Space, Popconfirm, Tooltip, Input, Card, Flex, Grid, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, SearchOutlined, ExportOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getDataExports, deleteDataExport } from './DataExportImportServices';
import { Resource, Action } from '../../Constants/socket';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocket from '../../Hooks/useSocket';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import DataExportForm from './DataExportForm';
import { RESOURCE_LABEL_MAP } from './constants';
import AppTable from '../Shared/AppTable/AppTable';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const DataExportList = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { user: { id: currentUserId } = {} } = useGlobal();
    const { socket } = useSocket();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Subscribe to DATA_EXPORT resource
    const exportIds = useMemo(() => data.map(item => item.id), [data]);
    useSocketSubscription(Resource.DATA_EXPORT, exportIds);

    // Listen for updates
    const handleExportUpdate = useCallback((updatedExport) => {
        setData(prevData => prevData.map(item => {
            if (item.id === updatedExport.id) {
                return {
                    ...item,
                    ...updatedExport,
                    status: {
                        ...(item.status || {}),
                        message_code: updatedExport.status_message_code || item.status?.message_code,
                        message_data: updatedExport.status_message_data || item.status?.message_data,
                    },
                    progress: updatedExport.progress !== undefined ? updatedExport.progress : item.progress
                };
            }
            return item;
        }));
    }, []);

    const handleExportCreate = useCallback((data) => {
        fetchData(pagination.current, searchText);

        // Subscribe to new export from server_datetime to cover the readiness gap
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.DATA_EXPORT, [data.id], data.server_datetime);
        }
    }, [pagination.current, searchText, socket]);

    useSocketEvent(Resource.DATA_EXPORT, Action.UPDATE, handleExportUpdate);
    useSocketEvent(Resource.DATA_EXPORT, Action.CREATE, handleExportCreate);

    const fetchData = useCallback(async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await getDataExports({
                page,
                page_size: pagination.pageSize,
                sort_by: 'created_at',
                sort_direction: 'desc',
            });
            setData(response.data);
            setPagination(prev => ({
                ...prev,
                current: response.metadata.page,
                total: response.metadata.total
            }));
        } catch (error) {
            message.error(t('failed_to_fetch_exports'));
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize, t]);

    const debouncedFetch = useMemo(
        () => debounce((page, search) => {
            fetchData(page, search);
        }, 500),
        [fetchData]
    );

    useEffect(() => {
        fetchData(pagination.current, searchText);
    }, [fetchData]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedFetch(1, value);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const response = await deleteDataExport(id);
            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);
            fetchData(pagination.current, searchText);
        } catch (error) {
            message.error(t('failed_to_delete_export'));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString(),
            width: 180,
        },
        {
            title: t('resources'),
            dataIndex: 'resources',
            key: 'resources',
            render: (resources) => resources?.map(r => <Tag key={r}>{t(RESOURCE_LABEL_MAP[r] || r)}</Tag>),
        },
        {
            title: t('format'),
            dataIndex: 'format',
            key: 'format',
            render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>,
            width: 100,
        },

        {
            title: t('progress'),
            dataIndex: 'progress',
            key: 'progress',
            width: 120,
            render: (progress, record) => {
                const statusObj = record.status;
                let status = 'unknown';
                if (statusObj && typeof statusObj === 'object' && statusObj.message_code) {
                    const parts = statusObj.message_code.split('.');
                    status = parts[parts.length - 1];
                } else if (typeof statusObj === 'string') {
                    status = statusObj;
                }

                const isProcessing = status === 'processing' || status === 'fetching_resource';
                const isCompleted = status === 'completed';
                const isFailed = status === 'failed';

                if (isCompleted) {
                    return <Text style={{ color: '#52c41a', fontWeight: 600 }}>100%</Text>;
                }
                if (isFailed) {
                    return <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>{Math.round(progress || 0)}%</Text>;
                }
                if (isProcessing && progress != null) {
                    return <Text style={{ color: '#1677ff', fontWeight: 600 }}>{Math.round(progress)}%</Text>;
                }
                return <Text type="secondary">—</Text>;
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => {
                const statusObj = record.status;
                let isCompleted = false;
                if (statusObj && typeof statusObj === 'object' && statusObj.message_code) {
                    isCompleted = statusObj.message_code.endsWith('completed');
                } else {
                    isCompleted = statusObj === 'completed';
                }

                return (
                    <Space>
                        {isCompleted && record.download_url && (
                            <Tooltip title={t('download')}>
                                <Button
                                    type="text"
                                    icon={<DownloadOutlined />}
                                    href={record.download_url}
                                    target="_blank"
                                />
                            </Tooltip>
                        )}
                        <Popconfirm
                            title={t('delete_export_confirm')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        }
    ];

    const expandedRowRender = (record) => {
        if (!record.summary?.resources) return null;

        const resources = Object.entries(record.summary.resources);
        const totalSummary = record.summary.total;

        // Resolve status for the header tag
        const statusObj = record.status;
        let status = 'unknown';
        if (statusObj && typeof statusObj === 'object') {
            if (statusObj.message_code) {
                const parts = statusObj.message_code.split('.');
                status = parts[parts.length - 1];
            } else if (statusObj.message) {
                status = statusObj.message;
            }
        } else if (typeof statusObj === 'string') {
            status = statusObj;
        }
        const isProcessing = status === 'processing' || status === 'fetching_resource';
        const isCompleted = status === 'completed';
        const isFailed = status === 'failed';
        let tagColor = 'default';
        if (isCompleted) tagColor = 'success';
        else if (isFailed) tagColor = 'error';
        else if (isProcessing) tagColor = 'processing';

        return (
            <div style={{ padding: '12px 48px 4px' }}>
                {/* Status */}
                <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{t('status')}:</Text>
                    <Tag color={tagColor} style={{ margin: 0 }}>
                        {statusObj && typeof statusObj === 'object'
                            ? resolveBackendMessage(t, statusObj)
                            : status}
                    </Tag>
                </Flex>

                {/* Summary total header */}
                {totalSummary && (
                    <Flex
                        justify="space-between"
                        align="center"
                        style={{ marginBottom: 8 }}
                    >
                        <Text strong style={{ fontSize: 13 }}>{t('total_progress', 'Total Progress')}</Text>
                        <Space size="large">
                            <span style={{ fontSize: 12, color: '#52c41a' }}>
                                {t('exported')}: <Text strong style={{ color: '#52c41a' }}>{totalSummary.exported?.toLocaleString()}</Text>
                            </span>
                            <span style={{ fontSize: 12, color: totalSummary.errors > 0 ? '#ff4d4f' : '#bfbfbf' }}>
                                {t('errors', 'Errors')}: <Text strong style={{ color: totalSummary.errors > 0 ? '#ff4d4f' : '#bfbfbf' }}>{totalSummary.errors?.toLocaleString()}</Text>
                            </span>
                            <span style={{ fontSize: 12, color: '#595959' }}>
                                {t('total')}: <Text strong>{totalSummary.total_records?.toLocaleString()}</Text>
                            </span>
                        </Space>
                    </Flex>
                )}

                {/* Global progress bar */}
                {record.progress != null && (
                    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                        <Progress
                            percent={Math.round(record.progress || 0)}
                            strokeColor={Math.round(record.progress || 0) >= 100 ? '#52c41a' : '#1677ff'}
                            size={[null, 8]}
                        />
                    </div>
                )}

                {/* Per-resource rows */}
                {resources.map(([resourceName, resourceData], index) => {
                    const percent = resourceData.total_records > 0
                        ? Math.round((resourceData.exported / resourceData.total_records) * 100)
                        : 0;
                    const hasErrors = resourceData.errors > 0;
                    const isResourceCompleted = resourceData.completed;
                    const color = hasErrors ? '#ff4d4f' : isResourceCompleted ? '#52c41a' : '#1677ff';
                    const resourceLabel = t(RESOURCE_LABEL_MAP[resourceName] || resourceName, resourceName.replace(/_/g, ' '));

                    return (
                        <div
                            key={resourceName}
                            style={{
                                paddingBottom: 12,
                                marginBottom: 12,
                                borderBottom: index < resources.length - 1 ? '1px dashed #f0f0f0' : 'none',
                            }}
                        >
                            <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
                                <Flex align="center" gap={8}>
                                    <div style={{ width: 3, height: 14, borderRadius: 2, background: color }} />
                                    <Text strong style={{ fontSize: 13, textTransform: 'capitalize' }}>
                                        {resourceLabel}
                                    </Text>
                                    <Tag
                                        color={hasErrors ? 'error' : isResourceCompleted ? 'success' : 'processing'}
                                        style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
                                    >
                                        {hasErrors ? t('errors', 'Errors') : isResourceCompleted ? t('completed') : t('processing')}
                                    </Tag>
                                </Flex>
                                <Space size="middle" style={{ fontSize: 12 }}>
                                    <span style={{ color: '#52c41a' }}>
                                        {t('exported')}: <Text strong style={{ color: '#52c41a' }}>{resourceData.exported?.toLocaleString()}</Text>
                                    </span>
                                    <span style={{ color: hasErrors ? '#ff4d4f' : '#bfbfbf' }}>
                                        {t('errors', 'Errors')}: <Text strong style={{ color: hasErrors ? '#ff4d4f' : '#bfbfbf' }}>{resourceData.errors?.toLocaleString()}</Text>
                                    </span>
                                    <span style={{ color: '#595959' }}>
                                        {t('total')}: <Text strong>{resourceData.total_records?.toLocaleString()}</Text>
                                    </span>
                                </Space>
                            </Flex>
                            <Progress
                                percent={percent}
                                size={[null, 6]}
                                strokeColor={color}
                                trailColor="#f0f0f0"
                                showInfo={false}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };


    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize }));
        fetchData(page, searchText);
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <DataExportForm open={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <Card
                title={t('exports')}
                extra={
                    <Button
                        icon={<ExportOutlined />}
                        onClick={() => setIsModalOpen(true)}
                        type="primary"
                    >
                        {!screens.xs && t('create_export')}
                    </Button>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="space-between"
                    style={{ width: '100%' }}
                >
                    <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_exports', 'Search exports')}
                            allowClear
                            value={searchText}
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>
                </Flex>
            </Card>
            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <AppTable
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => !!record.summary?.resources,
                    }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: handlePageChange,
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </Space>
    );
};

export default DataExportList;
